import * as React from 'react';
import { connect } from 'react-redux';
import isNil from 'lodash/isNil';

import { ModalFrame, ModalFrameInner } from 'components/modal';
import { Form, FormActions, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import ActionButton from 'components/buttons/action-button/ActionButton';

import { saveCcmCampaignMetadata } from 'modules/campaigns/components/ccm/redux/actions';

import { __t, applyTranslations } from 'utils/language';
import {
  isNcode,
  isRequired,
  isUuid
} from 'utils/validators';

import './ccm-campaign-metadata-modal.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./ccm-campaign-metadata-modal.i18n.json'));

import type { PromiseAction } from 'types';
import type { CcmCampaignMetadata } from 'modules/campaigns/components/ccm/types';

type OwnProps = {
  campaignId: number,
  onCloseModal: () => PromiseAction
};

type DispatchProps = {
  dispatchSaveMetadata: (campaignId: number, metadata: CcmCampaignMetadata) => Promise<void>
};

type Props = OwnProps & DispatchProps;

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'ccm_campaign_metadata_modal';

const CcmCampaignMetadataModal: React.FC<Props> = ({
  campaignId,
  dispatchSaveMetadata,
  onCloseModal
}): JSX.Element => {
  const [nCode, setNcode] = React.useState('');
  const [insertionOrderId, setInsertionOrderId] = React.useState('');

  const handleFieldChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const target = event.currentTarget;
    const identifier = target.dataset.identifier || null;
    const value = target.value;

    if (isNil(identifier)) {
      return;
    }

    if (identifier === 'nCode') {
      setNcode(value);
    }

    if (identifier === 'insertionOrderId') {
      setInsertionOrderId(value);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    await dispatchSaveMetadata(campaignId, { nCode, insertionOrderId });
    onCloseModal();
  };

  const renderButtons = (): React.ReactNode => (
    <FormActions
      align="center"
      containerClass="ccm-campaign-metadata-modal__form-actions"
    >
      <ActionButton
        color="tertiary"
        onClick={onCloseModal}
      >
        {__t(`${COMMON_MSG_KEY}.action_button.cancel`)}
      </ActionButton>
      <ActionButton
        color="primary"
        type="submit"
      >
        {__t(`${COMMON_MSG_KEY}.action_button.submit`)}
      </ActionButton>
    </FormActions>
  );

  const renderNcode = (): React.ReactNode => (
    <FormField
      showRequiredMark
      showValidationIcon={false}
      fontSize="small"
      label={__t(`${MSG_KEY}.labels.n_code`)}
      labelType="highlighted"
      direction="column"
      validators={[
        isRequired(nCode),
        isNcode(nCode.trim())
      ]}
    >
      <Input
        name="nCode"
        width="responsive"
        value={nCode}
        placeholder={__t(`${MSG_KEY}.placeholders.n_code`)}
        data={{ identifier: 'nCode' }}
        onChange={handleFieldChange}
      />
    </FormField>
  );

  const renderInsertionOrderId = (): React.ReactNode => (
    <FormField
      showRequiredMark
      showValidationIcon={false}
      fontSize="small"
      label={__t(`${MSG_KEY}.labels.insertion_order_id`)}
      labelType="highlighted"
      direction="column"
      validators={[
        isRequired(insertionOrderId),
        isUuid(insertionOrderId.trim())
      ]}
    >
      <Input
        name="insertionOrderId"
        width="responsive"
        value={insertionOrderId}
        placeholder={__t(`${MSG_KEY}.placeholders.insertion_order_id`)}
        data={{ identifier: 'insertionOrderId' }}
        onChange={handleFieldChange}
      />
    </FormField>
  );

  return (
    <div className="ccm-campaign-metadata-modal">
      <ModalFrame
        title={__t(`${MSG_KEY}.title`)}
      >
        <ModalFrameInner>
          <div className="ccm-campaign-metadata-modal__inner">
            <Form
              onSubmit={handleSubmit}
            >
              {renderNcode()}
              {renderInsertionOrderId()}
              {renderButtons()}
            </Form>
          </div>
        </ModalFrameInner>
      </ModalFrame>
    </div>
  );
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchSaveMetadata: (campaignId: number, metadata: CcmCampaignMetadata) => (
    dispatch(saveCcmCampaignMetadata(campaignId, metadata))
  )
});

export default connect(null, mapDispatchToProps)(CcmCampaignMetadataModal);
